import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import {
  Badge,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import {
  buildListQuery,
  normalizePaginatedListResponse,
  type PaginatedApiResponse,
} from '../../../services/paginatedList';
import { deleteCrewLookup } from '../../../services/mutations';
import { CertCategoryFormPanel } from './CertCategoryFormPanel';
import { crewLookupApiBase, crewLookupListPath } from './registry';
import type { CrewLookupItem } from './useData';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { ReferenceSidebarCollapseButton } from '../reference-workspaces/ReferenceSidebarChrome';

const UNCATEGORIZED_ID = -1;
const PANEL_WIDTH_KEY = 'cert-catalogs-panel-width';
const PANEL_MIN = 240;
const PANEL_MAX = 520;
const PANEL_DEFAULT = 320;
const SELECTED_CAT_KEY = 'cert-catalogs-selected-category';

const loadPanelWidth = (): number => {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = raw != null ? Number(raw) : PANEL_DEFAULT;
    if (!Number.isFinite(n)) return PANEL_DEFAULT;
    return Math.min(PANEL_MAX, Math.max(PANEL_MIN, n));
  } catch {
    return PANEL_DEFAULT;
  }
};

const loadSelectedCategoryId = (): number | null => {
  try {
    const raw = sessionStorage.getItem(SELECTED_CAT_KEY);
    if (raw == null || raw === '') return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

const CertCatalogWorkspace: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const confirm = useConfirm();
  const { can } = useAuth();

  const canEdit = can('crew_parameters', 'edit');
  const canDelete = can('crew_parameters', 'delete');

  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'sortOrder', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc',
    },
  );
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(`${PANEL_WIDTH_KEY}:collapsed`) === '1';
    } catch {
      return false;
    }
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(() => loadSelectedCategoryId());
  const [deleteCategory, setDeleteCategory] = useState<CrewLookupItem | null>(null);
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [categoryFormEdit, setCategoryFormEdit] = useState<CrewLookupItem | null>(null);

  const resizingRef = useRef(false);
  const panelWidthRef = useRef(panelWidth);
  const splitRef = useRef<HTMLDivElement>(null);
  const [panelResizing, setPanelResizing] = useState(false);

  const categoriesUrl = `${crewLookupApiBase('cert-categories')}${buildListQuery(undefined, 1, 500)}`;
  const catalogsUrl = `${crewLookupApiBase('cert-catalogs')}${buildListQuery(undefined, 1, 2000)}`;

  const {
    data: categoriesRaw,
    isLoading: catsLoading,
    mutate: mutateCats,
  } = useSWR<PaginatedApiResponse<CrewLookupItem> | CrewLookupItem[]>(categoriesUrl, fetcher, {
    revalidateOnFocus: true,
  });
  const {
    data: catalogsRaw,
    isLoading: catalogsLoading,
    mutate: mutateCatalogs,
  } = useSWR<PaginatedApiResponse<CrewLookupItem> | CrewLookupItem[]>(catalogsUrl, fetcher, {
    revalidateOnFocus: true,
  });

  const categories = useMemo(() => {
    const rows = normalizePaginatedListResponse<CrewLookupItem>(categoriesRaw, 1, 500).data;
    return [...rows].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
    );
  }, [categoriesRaw]);

  const allCatalogs = useMemo(
    () => normalizePaginatedListResponse<CrewLookupItem>(catalogsRaw, 1, 2000).data,
    [catalogsRaw],
  );

  const filteredCategories = useMemo(() => {
    const q = categoryFilter.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.code?.toLowerCase().includes(q) ?? false),
    );
  }, [categories, categoryFilter]);

  const uncategorizedCount = useMemo(
    () => allCatalogs.filter((c) => c.categoryId == null).length,
    [allCatalogs],
  );

  useEffect(() => {
    try {
      if (selectedCategoryId == null) sessionStorage.removeItem(SELECTED_CAT_KEY);
      else sessionStorage.setItem(SELECTED_CAT_KEY, String(selectedCategoryId));
    } catch {
      /* ignore */
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    try {
      localStorage.setItem(`${PANEL_WIDTH_KEY}:collapsed`, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    if (catsLoading) return;
    if (selectedCategoryId === UNCATEGORIZED_ID) return;
    if (selectedCategoryId != null && categories.some((c) => c.id === selectedCategoryId)) return;
    if (categories.length > 0) {
      setSelectedCategoryId(categories[0].id);
      return;
    }
    if (uncategorizedCount > 0) {
      setSelectedCategoryId(UNCATEGORIZED_ID);
      return;
    }
    setSelectedCategoryId(null);
  }, [categories, catsLoading, selectedCategoryId, uncategorizedCount]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const next = Math.min(PANEL_MAX, Math.max(PANEL_MIN, e.clientX - rect.left));
      setPanelWidth(next);
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      setPanelResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidthRef.current));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const selectedCategory = useMemo(() => {
    if (selectedCategoryId == null || selectedCategoryId === UNCATEGORIZED_ID) return null;
    return categories.find((c) => c.id === selectedCategoryId) ?? null;
  }, [categories, selectedCategoryId]);

  const linkedCatalogs = useMemo(() => {
    if (selectedCategoryId == null) return [];
    const q = searchTerm.trim().toLowerCase();
    return allCatalogs.filter((item) => {
      const matchCat =
        selectedCategoryId === UNCATEGORIZED_ID
          ? item.categoryId == null
          : item.categoryId === selectedCategoryId;
      if (!matchCat) return false;
      if (!q) return true;
      return (
        item.name.toLowerCase().includes(q) ||
        (item.code?.toLowerCase().includes(q) ?? false) ||
        (item.description?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [allCatalogs, selectedCategoryId, searchTerm]);

  const sortedCatalogs = useMemo(() => {
    const rows = [...linkedCatalogs];
    const { key, direction } = sortConfig;
    const dir = direction === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[key];
      const bv = (b as unknown as Record<string, unknown>)[key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return (Number(av) - Number(bv)) * dir;
      }
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
    return rows;
  }, [linkedCatalogs, sortConfig]);

  const total = sortedCatalogs.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedCatalogs.slice(start, start + itemsPerPage);
  }, [sortedCatalogs, page, itemsPerPage]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchTerm(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedCategoryId, itemsPerPage, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const categoryCanDelete = useCallback(
    (cat: CrewLookupItem) => !allCatalogs.some((item) => item.categoryId === cat.id),
    [allCatalogs],
  );

  const selectCategory = useCallback((id: number) => {
    setSelectedCategoryId(id);
    setSearchInput('');
    setSearchTerm('');
    setCategoryFormOpen(false);
  }, []);

  const startResize = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    resizingRef.current = true;
    setPanelResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const openCreateCategory = useCallback(() => {
    setCategoryFormEdit(null);
    setCategoryFormOpen(true);
  }, []);

  const openEditCategory = useCallback((row: CrewLookupItem) => {
    setSelectedCategoryId(row.id);
    setCategoryFormEdit(row);
    setCategoryFormOpen(true);
  }, []);

  const closeCategoryForm = useCallback(() => setCategoryFormOpen(false), []);

  const handleDeleteCatalog = async (e: React.MouseEvent, row: CrewLookupItem) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Delete catalog entry',
      message: `Delete “${row.name}”?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteCrewLookup('cert-catalogs', row.id);
      await mutateCatalogs();
      showSuccess('Catalog entry deleted');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not delete catalog entry.');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await deleteCrewLookup('cert-categories', id);
      await mutateCats();
      if (selectedCategoryId === id) setSelectedCategoryId(null);
      setDeleteCategory(null);
      setCategoryFormOpen(false);
      showSuccess('Category deleted');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not delete category.');
    }
  };

  const catalogListPath = crewLookupListPath('cert-catalogs');
  const newCatalogHref = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedCategoryId != null && selectedCategoryId !== UNCATEGORIZED_ID) {
      params.set('categoryId', String(selectedCategoryId));
    }
    const qs = params.toString();
    return qs ? `${catalogListPath}/new?${qs}` : `${catalogListPath}/new`;
  }, [catalogListPath, selectedCategoryId]);

  const catalogsEnabled = selectedCategoryId != null;
  const isLoading = catsLoading || catalogsLoading;
  const headerTitle =
    selectedCategoryId === UNCATEGORIZED_ID
      ? 'Uncategorized'
      : selectedCategory?.name ?? 'Select a category';

  const clearCatalogSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  return (
    <div ref={splitRef} className={cn('flex h-full min-h-0 gap-1 p-1', panelResizing && 'select-none')}>
      {sidebarCollapsed ? (
        <aside
          className={cn(
            'flex w-11 shrink-0 flex-col items-center rounded-2xl border py-2 shadow-sm',
            colors.surface,
            colors.border,
          )}
        >
          <ReferenceSidebarCollapseButton collapsed onToggle={() => setSidebarCollapsed(false)} />
          <span
            className={cn(
              'mt-2 text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180',
              colors.textMuted,
            )}
          >
            Categories
          </span>
        </aside>
      ) : (
      <aside
        className={cn(
          'shrink-0 flex flex-col min-h-0 overflow-hidden rounded-2xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
        style={{ width: panelWidth }}
      >
          <div className={cn('px-3 py-2.5 border-b flex items-center justify-between gap-2', colors.border)}>
            <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
              Categories
            </p>
            <div className="flex items-center gap-1">
            {canEdit && (
              <button
                type="button"
                onClick={openCreateCategory}
                className={cn(
                  'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors text-indigo-600',
                  'hover:bg-indigo-50 dark:hover:bg-indigo-950/40',
                )}
                title="New category"
                aria-label="New category"
              >
                <PlusIcon className="h-3.5 w-3.5" />
              </button>
            )}
            <ReferenceSidebarCollapseButton collapsed={false} onToggle={() => setSidebarCollapsed(true)} />
            </div>
          </div>

          <div className={cn('px-3 py-2 border-b', colors.border)}>
            <div className="relative">
              <SearchIcon
                className={cn(
                  'absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none',
                  colors.textMuted,
                )}
              />
              <input
                type="search"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                placeholder="Filter..."
                className={cn(
                  'w-full h-8 pl-8 pr-3 rounded-md border text-sm',
                  colors.border,
                  colors.surface,
                  colors.text,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                )}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto dashboard-scrollbar py-1">
            {catsLoading && (
              <p className={cn('px-3 py-4 text-sm', colors.textMuted)}>Loading categories...</p>
            )}

            {!catsLoading && categories.length === 0 && uncategorizedCount === 0 && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>
                No categories yet.
                {canEdit ? ' Use + to create one.' : ''}
              </p>
            )}

            {!catsLoading && !categoryFilter.trim() && (uncategorizedCount > 0 || categories.length === 0) && (
              <div
                role="option"
                aria-selected={selectedCategoryId === UNCATEGORIZED_ID && !categoryFormOpen}
                onClick={() => selectCategory(UNCATEGORIZED_ID)}
                className={cn(
                  'group/row flex items-center gap-1 h-9 cursor-pointer select-none transition-colors px-2',
                  selectedCategoryId === UNCATEGORIZED_ID && !categoryFormOpen
                    ? 'bg-blue-600 text-white'
                    : cn(colors.text, 'hover:bg-slate-100 dark:hover:bg-slate-800/50'),
                )}
              >
                <span
                  className={cn(
                    'w-5 shrink-0 inline-flex items-center justify-center',
                    selectedCategoryId === UNCATEGORIZED_ID && !categoryFormOpen
                      ? 'text-white'
                      : 'text-slate-400',
                  )}
                >
                  <FolderIcon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm italic">Uncategorized</span>
                <span
                  className={cn(
                    'text-xs tabular-nums shrink-0',
                    selectedCategoryId === UNCATEGORIZED_ID && !categoryFormOpen
                      ? 'text-white/70'
                      : colors.textMuted,
                  )}
                >
                  {uncategorizedCount}
                </span>
              </div>
            )}

            {!catsLoading &&
              filteredCategories.map((row) => {
                const active = selectedCategoryId === row.id && !categoryFormOpen;
                const count = allCatalogs.filter((c) => c.categoryId === row.id).length;
                const deletable = categoryCanDelete(row);
                const actionBtn = active
                  ? 'text-white/85 hover:text-white hover:bg-white/15'
                  : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 dark:hover:text-slate-200 dark:hover:bg-slate-700/60';
                return (
                  <div
                    key={row.id}
                    role="option"
                    aria-selected={selectedCategoryId === row.id}
                    onClick={() => selectCategory(row.id)}
                    className={cn(
                      'group/row flex items-center gap-1 h-9 cursor-pointer select-none transition-colors',
                      active
                        ? 'bg-blue-600 text-white'
                        : cn(colors.text, 'hover:bg-slate-100 dark:hover:bg-slate-800/50'),
                      !row.isActive && !active && 'opacity-60',
                    )}
                    style={{ paddingLeft: 8, paddingRight: 6 }}
                    title={row.name}
                  >
                    <span
                      className={cn(
                        'w-5 shrink-0 inline-flex items-center justify-center',
                        active ? 'text-white' : 'text-violet-500 dark:text-violet-400',
                      )}
                    >
                      <FolderIcon className="h-3.5 w-3.5" />
                    </span>
                    <span className={cn('min-w-0 flex-1 truncate text-sm', active && 'font-medium')}>
                      {row.name}
                    </span>
                    <span
                      className={cn(
                        'text-xs tabular-nums shrink-0 mr-0.5',
                        active ? 'text-white/70' : colors.textMuted,
                      )}
                    >
                      {count}
                    </span>
                    {(canEdit || canDelete) && (
                      <span
                        className={cn(
                          'shrink-0 flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100',
                          selectedCategoryId === row.id && 'opacity-100',
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canEdit && (
                          <button
                            type="button"
                            className={cn('p-1 rounded', actionBtn)}
                            title="Edit category"
                            aria-label="Edit category"
                            onClick={() => openEditCategory(row)}
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            disabled={!deletable}
                            className={cn(
                              'p-1 rounded',
                              deletable
                                ? active
                                  ? 'text-white/85 hover:bg-white/15'
                                  : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40'
                                : active
                                  ? 'text-white/30 cursor-not-allowed'
                                  : 'text-slate-300 dark:text-slate-600 cursor-not-allowed',
                            )}
                            title={
                              deletable
                                ? 'Delete category'
                                : 'Cannot delete — category still has catalog entries'
                            }
                            aria-label="Delete category"
                            onClick={() => setDeleteCategory(row)}
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}

            {!catsLoading && categoryFilter.trim() && filteredCategories.length === 0 && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>No categories match.</p>
            )}
          </div>
        </aside>
      )}

      {!sidebarCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={panelWidth}
          aria-label="Resize categories panel"
          title="Drag to resize"
          onMouseDown={startResize}
          className={cn(
            'flex shrink-0 w-1.5 cursor-col-resize items-center justify-center group rounded-full',
            'hover:bg-indigo-500/20 active:bg-indigo-500/30',
            panelResizing && 'bg-indigo-500/30',
          )}
        >
          <div className="w-0.5 h-12 rounded-full bg-slate-400 dark:bg-slate-500 opacity-60 group-hover:opacity-100" />
        </div>
      )}

        <div className="relative min-h-0 min-w-0 flex-1">
          {sidebarCollapsed && (
            <div className="absolute left-2 top-2 z-10">
              <ReferenceSidebarCollapseButton collapsed onToggle={() => setSidebarCollapsed(false)} />
            </div>
          )}
          {categoryFormOpen ? (
            <div
              className={cn(
                'h-full min-h-0 overflow-auto rounded-2xl border shadow-sm',
                colors.surface,
                colors.border,
              )}
            >
              <CertCategoryFormPanel
                editItem={categoryFormEdit}
                onCancel={closeCategoryForm}
                onSaved={async (saved) => {
                  await mutateCats();
                  setSelectedCategoryId(saved.id);
                  closeCategoryForm();
                  showSuccess(categoryFormEdit ? 'Category saved' : 'Category created');
                }}
              />
            </div>
          ) : (
            <GeneralList
              className="h-full"
              title={headerTitle}
              description={
                catalogsEnabled
                  ? `${isLoading ? '…' : total} catalog entr${total === 1 ? 'y' : 'ies'}`
                  : 'Select a category'
              }
              search={{
                value: searchInput,
                onChange: setSearchInput,
                placeholder: 'Search catalog entries…',
              }}
              actions={
                canEdit && catalogsEnabled ? (
                  <Link
                    to={newCatalogHref}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
                      colors.primary,
                    )}
                  >
                    <PlusIcon className="h-4 w-4" />
                    New catalog
                  </Link>
                ) : null
              }
              hasFilters={Boolean(searchTerm.trim())}
              onClearFilters={clearCatalogSearch}
              sorting={{ sortConfig, onSort: handleSort }}
              isLoading={catalogsEnabled && isLoading}
              loadingLabel="Loading catalog entries…"
              isEmpty={!isLoading && (!catalogsEnabled || pageRows.length === 0)}
              emptyState={{
                icon: <DocumentTextIcon className="h-12 w-12" />,
                title: !catalogsEnabled
                  ? 'Select a category'
                  : searchTerm.trim()
                    ? 'No catalog entries match'
                    : 'No catalog entries yet',
                hint: !catalogsEnabled
                  ? 'Choose a category on the left to view its catalog entries.'
                  : searchTerm.trim()
                    ? 'Try adjusting the search.'
                    : 'Add a certificate catalog entry for this category.',
                action: !catalogsEnabled ? undefined : searchTerm.trim() ? (
                  <button
                    type="button"
                    onClick={clearCatalogSearch}
                    className={cn(
                      'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                      colors.border,
                      colors.text,
                    )}
                  >
                    Clear search
                  </button>
                ) : canEdit ? (
                  <Link
                    to={newCatalogHref}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                      colors.primary,
                    )}
                  >
                    <PlusIcon className="h-4 w-4" />
                    New catalog
                  </Link>
                ) : undefined,
              }}
              pagination={
                catalogsEnabled
                  ? {
                      page,
                      totalPages,
                      total,
                      itemsPerPage,
                      pageSizeOptions,
                      onPageChange: setPage,
                      onItemsPerPageChange: (size) => {
                        setItemsPerPage(size);
                        setPage(1);
                      },
                    }
                  : undefined
              }
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                    <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
                    <GeneralListSortableTh sortKey="code">Code</GeneralListSortableTh>
                    <GeneralListSortableTh sortKey="name">Name</GeneralListSortableTh>
                    <GeneralListSortableTh sortKey="sortOrder">Order</GeneralListSortableTh>
                    <GeneralListSortableTh sortKey="isActive">Active</GeneralListSortableTh>
                    <GeneralListSortableTh sortKey="createdAt">Created</GeneralListSortableTh>
                    <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`${catalogListPath}/${row.id}`)}
                      className={cn(
                        'cursor-pointer border-b transition-colors last:border-b-0',
                        colors.border,
                        colors.text,
                        colors.interactiveHover,
                        !row.isActive && 'opacity-60',
                      )}
                    >
                      <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-0.5">
                          <Link
                            to={`${catalogListPath}/${row.id}`}
                            className={cn(
                              'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                              colors.textMuted,
                              colors.interactiveHover,
                            )}
                            title="View"
                            aria-label="View"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          {canEdit ? (
                            <Link
                              to={`${catalogListPath}/${row.id}/edit`}
                              className={cn(
                                'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                                colors.textMuted,
                                colors.interactiveHover,
                              )}
                              title="Edit"
                              aria-label="Edit"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteCatalog(e, row)}
                              className={cn(
                                'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                                colors.textMuted,
                                'hover:text-red-600',
                              )}
                              title="Delete"
                              aria-label="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className={cn('pr-6 whitespace-nowrap font-mono text-xs', colors.textSecondary)}>
                        {row.code || ''}
                      </td>
                      <td className="pr-6 font-medium whitespace-nowrap">{row.name}</td>
                      <td className={cn('pr-6 whitespace-nowrap tabular-nums', colors.textSecondary)}>
                        {row.sortOrder ?? 0}
                      </td>
                      <td className="pr-6 whitespace-nowrap">
                        <Badge variant={row.isActive ? 'success' : 'danger'}>
                          {row.isActive ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td className={cn('pr-6 whitespace-nowrap', colors.textSecondary)}>
                        {row.createdAt ? formatDateGlobal(row.createdAt) : ''}
                      </td>
                      <td className="w-full" aria-hidden />
                    </tr>
                  ))}
                </tbody>
              </table>
            </GeneralList>
          )}
        </div>

      {deleteCategory != null && (
        <ConfirmationDialog
          isOpen
          onClose={() => setDeleteCategory(null)}
          onConfirm={() => handleDeleteCategory(deleteCategory.id)}
          title="Delete category"
          message={
            categoryCanDelete(deleteCategory)
              ? `Delete category “${deleteCategory.name}”?`
              : 'This category still has catalog entries. Move or delete them first.'
          }
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
        />
      )}
    </div>
  );
};

export default CertCatalogWorkspace;
